import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCampaignConfigSettings } from '../services/campaign';
import { saveBannerConfigSettings } from '../services/bannerRules';
import type { BannerSettingRow } from '../services/bannerRules';
import { campaignConfigSettingKeys } from './useCampaignConfigSettingMutations';

/**
 * Bespoke query + save mutation for the Banner Rules tab, mirroring useCampaignTransferRules.
 * The query key is the SHARED campaignConfigSettingKeys.settings(campaignId) key (not a
 * bespoke banner-only key) so it dedupes with any other consumer of this campaign's settings
 * and the save mutation's invalidation lands correctly.
 */
export const useCampaignBannerRules = (campaignId?: string) => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: campaignConfigSettingKeys.settings(campaignId ?? 'unresolved'),
        queryFn: () => {
            if (!campaignId) {
                throw new Error('useCampaignBannerRules: campaignId is required to fetch banner rules');
            }
            return fetchCampaignConfigSettings(campaignId);
        },
        enabled: Boolean(campaignId),
    });

    const saveMutation = useMutation({
        mutationFn: (rows: BannerSettingRow[]) => {
            if (!campaignId) {
                throw new Error('useCampaignBannerRules: campaignId is required to save banner rules');
            }
            return saveBannerConfigSettings(campaignId, rows);
        },
        onSuccess: () => {
            if (!campaignId) return;
            queryClient.invalidateQueries({ queryKey: campaignConfigSettingKeys.settings(campaignId) });
        },
    });

    return { query, saveMutation };
};
